/**
 * i18n setup — Pure H Holding (Iteration 44).
 *
 * Languages: English (default) + Arabic with full RTL support.
 *
 * The `useLang()` hook below is a thin convenience around react-i18next's
 * useTranslation() that also returns the current direction so callers can
 * conditionally render right-aligned elements.
 *
 * Direction is applied to <html> on every language change so the global
 * Tailwind layout flips automatically (text-align, flex, scrollbars).
 */
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./en.json";
import ar from "./ar.json";

const STORAGE_KEY = "pureh.lang";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    fallbackLng: "en",
    supportedLngs: ["en", "ar"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  });

function applyDir(lang) {
  const dir = lang === "ar" ? "rtl" : "ltr";
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.classList.toggle("orv-rtl", dir === "rtl");
  }
}

// Apply on boot + every subsequent change.
applyDir(i18n.language || "en");
i18n.on("languageChanged", applyDir);

export function useLang() {
  const { t, i18n: instance } = useTranslation();
  const lang = instance.language || "en";
  const dir = lang === "ar" ? "rtl" : "ltr";
  function setLang(next) { instance.changeLanguage(next); }
  return { t, lang, dir, setLang, isRTL: dir === "rtl" };
}

export default i18n;
